import { Button, ButtonLevel, ButtonSize, Caption, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { CheckIcon, ChevronRightDoubleIcon, XCloseIcon } from '@vizit/icons';
import { Asset, Heading, HeadingVariant } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import { containerClasses, dividerClasses, gridClasses } from './styles';
import type { IdealAssetMixAsset, IdealAssetMixProps } from './types';

function renderAsset(asset: IdealAssetMixAsset) {
  const hasImage = !!asset.imageUrl;

  if (!hasImage) {
    return (
      <Asset key={asset.id} isViewOnly className="w-full h-full">
        <Asset.Label>{asset.label}</Asset.Label>
        <Asset.Image src={null} name={asset.label} />
      </Asset>
    );
  }

  if (asset.isInformational) {
    return (
      <Asset key={asset.id} isViewOnly isUnscored className="w-full h-full">
        <Asset.Label>{asset.label}</Asset.Label>
        <Asset.Image src={asset.imageUrl ?? null} name={asset.label} border />
      </Asset>
    );
  }

  return (
    <Asset key={asset.id} isViewOnly score={asset.score ?? null} className="w-full h-full">
      <Asset.Label>{asset.label}</Asset.Label>
      <Asset.Image src={asset.imageUrl ?? null} name={asset.label} border />
    </Asset>
  );
}

function IdealAssetMix(props: IdealAssetMixProps) {
  const { assetGroups, extraAssets, onShowAssets, isDrawerOpen, className } = props;
  const hasExtraAssets = extraAssets && extraAssets.length > 0;

  return (
    <div className={cx(containerClasses(), className)}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <Heading variant={HeadingVariant.H6}>Optimal asset mix</Heading>
            {onShowAssets && (
              <Button
                level={ButtonLevel.Ghost}
                size={ButtonSize.Sm}
                className="font-semibold"
                iconEnd={
                  isDrawerOpen ? (
                    <XCloseIcon size={20} className="text-quaternary" />
                  ) : (
                    <ChevronRightDoubleIcon size={20} className="text-quaternary" />
                  )
                }
                onPress={onShowAssets}
              >
                {isDrawerOpen ? 'Hide assets' : 'Show assets'}
              </Button>
            )}
          </div>
          <Text size={TextSize.Xs} weight={TextWeight.Medium} color={TextColor.Tertiary}>
            Match your carousel assets to the optimal mix shown below. Add any missing asset classes.
          </Text>
        </div>

        <div className="flex items-start gap-8">
          {assetGroups.length > 0 && (
            <div className="w-[118px] shrink-0">{assetGroups[0].assets.map((asset) => renderAsset(asset))}</div>
          )}
          {assetGroups.length > 1 && (
            <>
              <div className={dividerClasses()} />
              <div className="flex-1 min-w-0">
                <div className={gridClasses()}>
                  {assetGroups
                    .slice(1)
                    .flatMap((group) => group.assets)
                    .map((asset) => renderAsset(asset))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {hasExtraAssets ? (
        <Caption label="EXTRA ASSET CLASSES" gap="gap-6">
          <Text size={TextSize.Xs} weight={TextWeight.Medium} color={TextColor.Tertiary}>
            These assets are not part of the Vizit Standard for this category. Remove or move to end of carousel.
          </Text>
          <div className={gridClasses()}>{extraAssets?.map((asset) => renderAsset(asset))}</div>
        </Caption>
      ) : (
        <Caption label="EXTRA ASSET CLASSES" gap="gap-6">
          <div className="flex items-center gap-4">
            <div className="inline-flex size-4 shrink-0 items-center justify-center overflow-hidden bg-success-500 text-white">
              <CheckIcon size={16} color="white" />
            </div>
            <Text size={TextSize.Xs} weight={TextWeight.Medium} color={TextColor.Tertiary}>
              You have no extra asset classes. Nice work!
            </Text>
          </div>
        </Caption>
      )}
    </div>
  );
}

export { IdealAssetMix };
export type { IdealAssetMixProps };
